/**
 * @packageDocumentation
 * @module f1/database
 */

import { t_lens, t_key, t_pointer } from '../types';
import is_string from '../is/is_string';
import lens_fn from './lens';

export interface Read {
  /**
   * Read from an object with a lens
   *
   * @public
   * @see f1.lens uses a lens to read from an object
   * @function f1.read
   * @param lens a lens to look in to the object with
   * @param obj the object to read from
   * @example
   *
   * ```ts
   * f1.read(f1.lens('/p1'), 0, { p1: 1 }) === 1
   * f1.read(f1.lens('/p1/1'), 0, { p1: [1, 0] }) === 0
   * ```
   */
  <O, R>(lens: t_pointer | t_lens, safe: R, obj: O): R;
  <O, R>(lens: t_pointer | t_lens, safe: R): (obj: O) => R;
}

/**
 * @see Read
 */
export default <O, R>(lens: t_pointer | t_lens, safe: R, obj: O | R): R => {
  lens = is_string(lens) ? lens_fn(lens) : lens;

  const n = lens.length;
  if (n === 1) {
    return (obj ?? (safe as unknown)) as R;
  }

  let p = 1;
  let part: t_key;
  for (; p < n; ) {
    part = lens[p++];
    if (part === '') {
      continue;
    }
    if (obj == null || typeof obj !== 'object') {
      return safe;
    }

    obj = obj[part];

    if (n === p) {
      return (obj ?? (safe as unknown)) as R;
    }
  }

  return safe;
};
